<script setup>
import upperFirst from 'lodash/upperFirst'
import { ref, computed, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import PageLayout from '../Components/UI/PageLayout.vue'
import MainContainer from '../Components/UI/Content/MainContainer.vue'
import BlockContent from '../Components/UI/Content/BlockContent.vue'
import TableMenu from '../Components/UI/Table/TableMenu.vue'
import CardContent from '../Components/UI/Content/CardContent.vue'
import DataTable from '../Components/UI/Table/DataTable.vue'
import ModalError from '../Components/UI/Modal/ModalError.vue'
import { getWalletClientColumns } from '../Constants/ContractColumns'
import usePlatform from '../Composables/usePlatform'
import useApi from '../Composables/useApi'
import useUser from '../../../Composables/User/useUser.ts'
import useSearchParams from '../Composables/useSearchParams'
import useContractExport from '../Composables/useContractExport'
import _ from 'lodash'

const { t } = useI18n()
const { getJSON } = useApi()
const { isPlatformClient, isClient, isPlatform } = usePlatform()
const { user } = useUser()
const { searchParams } = useSearchParams()
const { exportContracts, canDownloadExport } = useContractExport()

const contracts = ref([])
const contractKeys = ref([])
const isLoaded = ref(false)
const selectedStatus = ref(parseInt(searchParams.get('s') || 0))
const errors = ref([])
const walletList = ref(null)

const filters = ref({
  units: [],
  platformSalesmen: [],
})

const filtersValues = ref({
  unit: null,
  platformSalesman: null,
})

const canDownload = computed(() => {
  return canDownloadExport(user.value?.roles || [])
})

const submenuLabels = computed(() => [
  { id: 0, name: t('contract.list.type.status.ALL') },
  { id: 1, name: t('contract.list.type.status.FINISH') },
  { id: 2, name: t('contract.list.type.status.ARCHIVE') },
])

const tableFilters = computed(() => {
  return [
    { name: 'unit', optionsKey: 'units', domain: 'simulation', labelTop: true, condition: true },
    {
      name: 'platformSalesman',
      optionsKey: 'platformSalesmen',
      domain: 'simulation',
      labelTop: true,
      condition: !isPlatformClient.value,
    },
  ].filter(({ condition }) => condition === true)
})

const getDynamicColumns = (opts = {}) => {
  const { withLink = true, isPlatformClientVal = false } = opts
  return contractKeys.value.map((key) => {
    const uppedKey = upperFirst(key)

    switch (key) {
      case 'id':
        return {
          data: key,
          displayable: false,
        }
      case 'number': {
        const col = {
          name: key,
          title: t(`contract.list.table.label${uppedKey}`),
        }
        if (withLink) {
          return {
            ...col,
            formatter: 'context',
            options: { renderType: 'portefeuille', renderId: 'id' },
          }
        }
        return col
      }
      case 'status':
        return {
          name: key,
          title: t(`contract.list.table.label${uppedKey}`),
          formatter: 'status',
          options: { renderType: 'contract' },
        }
      case 'orderNumber': {
        const path = isPlatformClientVal ? 'platformClient.' : ''
        return {
          name: key,
          title: t(`contract.list.table.${path}label${uppedKey}`),
        }
      }
      case 'platformSalesman':
        return {
          name: key,
          title: t(`contract.list.table.label${uppedKey}`),
        }
      case 'rentAmount':
      case 'totalAmount':
        return {
          name: key,
          title: t(`contract.list.table.label${uppedKey}`),
          type: 'num-fmt',
          formatter: 'currency',
        }
      case 'createdAt':
      case 'paidAt':
        return {
          name: key,
          title: t(`contract.list.table.label${uppedKey}`),
          type: 'date',
          formatter: 'date',
        }
      default:
        return {
          name: key,
          title: t(`contract.list.table.${key}`),
        }
    }
  })
}

const columns = computed(() => {
  return isPlatformClient.value && contractKeys.value.length > 0
    ? getDynamicColumns({
        withLink: true,
        isPlatformClientVal: isPlatformClient.value,
      })
    : getWalletClientColumns(t)
})

const updateParams = () => {
  let statuses = []
  switch (selectedStatus.value) {
    case 1:
      statuses.push('FINISH')
      break
    case 2:
      statuses.push('CLOSED')
      break
    default:
      statuses.push('FINISH', 'CLOSED')
  }
  searchParams.set('s', selectedStatus.value)
  searchParams.set('status', statuses.join('-'))
  fetchContracts()
}

const getUrl = () => {
  if (isPlatformClient.value) {
    searchParams.set('module', 'wallet')
  } else {
    searchParams.delete('module')
  }
  const path = isPlatformClient.value ? 'contracts/client' : 'contracts/wallet'
  return path + '?' + searchParams.toString()
}

const fetchContracts = async () => {
  contracts.value = await getJSON(getUrl())
  if (isPlatformClient.value && contractKeys.value.length === 0 && contracts.value.length > 0) {
    contractKeys.value = Object.keys(contracts.value[0])
  }
  isLoaded.value = true
  walletList.value?.renderDataTable()
}

const getContracts = async (callback) => {
  callback({ data: contracts.value })
}

const download = async () => {
  await exportContracts('contracts/export/client', searchParams)
}

const setDateTable = (createdAtValue, createdAtEndValue) => {
  if (createdAtValue) {
    searchParams.set('createdAt', createdAtValue)
  } else {
    searchParams.delete('createdAt')
  }
  if (createdAtEndValue) {
    searchParams.set('createdAtEnd', createdAtEndValue)
  } else {
    searchParams.delete('createdAtEnd')
  }
  fetchContracts()
}

const setSelectedStatus = (value) => {
  value = parseInt(value)
  if (value !== selectedStatus.value) {
    selectedStatus.value = value
    updateParams()
  }
}

const updateFilterData = (newFilters) => {
  filtersValues.value = { ...filtersValues.value, ...newFilters }

  for (const [name, value] of Object.entries(newFilters)) {
    const newValue = typeof value === 'string' || value > 0 ? value : null

    if (newValue) {
      searchParams.set(name, newValue)
    } else {
      searchParams.delete(name)
    }
  }
  fetchContracts()
}

onBeforeMount(() => {
  if (!(isClient.value || isPlatform.value)) {
    window.location = '/404.php'
  }
  updateParams()
})
</script>
<template>
  <PageLayout menu="wallet" client>
    <MainContainer :title-t="isPlatformClient ? 'menu.myPortfolio' : 'menu.myContracts'">
      <BlockContent>
        <TableMenu
          :status="selectedStatus"
          :submenu-labels="submenuLabels"
          :filters="tableFilters"
          :filter-values="filtersValues"
          :filter-options="filters"
          :show-download-button="isPlatformClient && canDownload"
          :show-filters-button="isPlatformClient"
          endpoint="contract"
          @download="download"
          @set-date="setDateTable"
          @update-filter-data="updateFilterData"
          @set-selected-status="setSelectedStatus"
        />
        <CardContent>
          <DataTable
            v-if="isLoaded"
            id="listeWallet"
            ref="walletList"
            :columns="columns"
            :fetch="getContracts"
            :order="[[2, 'desc']]"
            :zero-records="t('contract.list.zeroRecords')"
          />
        </CardContent>
      </BlockContent>
    </MainContainer>
    <ModalError v-if="errors.length > 0" id="modalError" ref="modalError" :errors="errors" />
  </PageLayout>
</template>
